---
title: RAG routing
summary: Point the app at one or more RAG servers, and expose cross-environment instances safely.
visibility: authenticated
audience: operator
section: Operating
order: 122
last_verified: 2026-10-25
owner: docs
---

# RAG routing

Personas answer by querying a RAG server. As an operator you decide which server(s) the
deployment can reach and what the default is.

## The deployment default

The frontend reads its default RAG base from the public feature manifest, which comes from
the `RAG_PROXY_BASE` environment variable (for example `/rag-proxy/ai-siemens-hybridrag`). On
Azure this is set for you by `bin/azure/setup.sh` from the environment's configured RAG
instances. A persona that leaves its server URL blank uses this default, in the browser and
in chat, which the backend sends on to the RAG server. Keep it a path, never a full URL: the
browser uses it too.

## Outside AWS and Azure

The backend sends a path such as `/rag-proxy` to `RAG_PROXY_ORIGIN`. On AWS and Azure that is
the proxy running beside the backend, so you leave it unset. Every other setup runs that proxy
somewhere else, so it sets both variables:

| Setup | `RAG_PROXY_BASE` | `RAG_PROXY_ORIGIN` |
|---|---|---|
| Docker Compose | `/rag-proxy` | `http://frontend:80` |
| Docker Compose for development | `/rag-proxy` | `http://frontend-dev:5173` |
| Edge-local | `/rag-proxy` | `http://127.0.0.1:3000` |
| Local development without Docker | `/rag-proxy` | `http://localhost:3000` |

The Compose files already set these. For local development, copy them from `.env.example`.
Named instances (`/rag-proxy/<instance>`, below) are narrower here. Both development setups
proxy through Vite, which serves only the default path. Docker Compose and edge-local serve
just the three instances their proxy is built with (`rag-patient-ed`, `rag-clinical-anatomy`
and `rag-human-optimization`); any other name is sent to the default server, which doesn't know
it. Cross-environment
aliases (below) work only on AWS and Azure.

## Same-environment instances

The nginx proxy can route to multiple RAG instances under `/rag-proxy/<instance>`. A persona's
**Server URL** can point at one of these to override the default within the same environment.

## Cross-environment aliases

To let personas reach a RAG server in another environment without browser CORS coordination,
register an alias:

```
RAG_EXTERNAL_ALIAS_JASO=https://jaso-app.<env-domain>.azurecontainerapps.io
```

At container startup an nginx `location` block is rendered for each alias, exposing it at
`/rag-external/<alias>`. Browsers stay same-origin; the target URLs are **not** exposed in the
public manifest (only the alias names are), so deployment topology doesn't leak. Authors then
pick the alias in **Persona Settings** (**AI Settings** tab).

## How a persona chooses a graph

Within a RAG server, a persona's **Graph ID** selects which knowledge graph to query (sent as
the `X-Graph-Id` header). See the author guide,
[Connect a persona to its knowledge](/help/how-to/connect-knowledge-rag).

## See also

- [Configuration](/help/operator/configuration) — the related environment variables.
